"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Bot, Sparkles, Database, Binary, BookOpen, Share2, ArrowRight, ShieldAlert, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function AiDiscovery() {
  const shouldReduceMotion = useReducedMotion();

  // The four core pillars that matter from the approved source
  const aiPillars = [{
    number: "01",
    icon: Database,
    title: "Structured product information",
    description: "Comprehensive product specifications, clear pricing, accurate availability, dimensions, materials, and high-resolution imagery. AI engines require unambiguous data points to recommend products accurately in conversational answers."
  }, {
    number: "02",
    icon: Binary,
    title: "Accurate schema and entity signals",
    description: "Valid Product, Offer, Review, and Organization schema markup that mirrors live inventory. Clear entity connections help AI crawlers verify your brand's authority, catalogue depth, and product relationships without guesswork."
  }, {
    number: "03",
    icon: BookOpen,
    title: "Helpful buying guides and FAQs",
    description: "Well-organised comparison guides, size selectors, compatibility notes, and direct answers to pre-sale questions. AI tools frequently pull context and recommendations from informational content that directly answers searchers' underlying questions."
  }, {
    number: "04",
    icon: Share2,
    title: "Credible web coverage and mentions",
    description: "Genuine brand citations, customer reviews across independent platforms, and industry editorial coverage across the web. AI search models rely on external corroboration to confirm a store's trust and product quality."
  }];
  return <section id="aiecommercediscovery" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Bot className="w-3.5 h-3.5 mr-1.5" />
            Next-Generation Search
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Ecommerce SEO and AI-assisted product discovery
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            AI-assisted search tools such as Google AI Overviews, ChatGPT, and Perplexity are changing
            how shoppers discover products and retail brands. Instead of clicking through traditional search
            result links, shoppers increasingly see synthesised recommendations and brand mentions.
          </p>
        </div>

        {/* Narrative Intro Card */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="border border-border/80 bg-card shadow-xs">
            <CardContent className="pt-6 sm:p-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <Search className="size-5" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold font-default text-foreground">
                    Why structured store data matters for AI search
                  </h3>
                  <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                    AI models do not browse visual store layouts like human shoppers; they parse and interpret structured catalogue data, contextual guides, and web-wide trust signals. Stores with clean technical foundations, accurate schema, and authoritative buying guides are better positioned to be surfaced in AI answers than stores relying on thin product copy or unorganised catalogues.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Four Key Pillars */}
        <div className="mb-14">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              What matters for AI product visibility
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Four fundamental areas I address to ensure your catalogue is structured for both traditional search engines and AI discovery tools:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {aiPillars.map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={`ai-pillar-${i}`} data-index={i} initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.2
            }} transition={{
              duration: 0.35,
              delay: shouldReduceMotion ? 0 : i * 0.08
            }} className="flex">
                  <Card className="flex w-full flex-col justify-between border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="size-4" />
                        </div>
                        <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                          {item.number}
                        </span>
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Explicit No Guarantees Notice (Mandatory from spec & source) */}
        <div className="max-w-4xl mx-auto p-5 rounded-xl border border-border bg-card/60 flex items-start gap-3.5">
          <ShieldAlert className="size-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="space-y-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
            <p className="font-medium text-foreground">
              Realistic expectations: No guaranteed AI citations
            </p>
            <p>
              There are no guarantees of being cited by AI tools. AI model outputs are probabilistic and constantly evolving. However, stores with clean catalogue data, accurate technical schema, helpful buying content, and genuine brand authority are better positioned than stores without them.
            </p>
          </div>
        </div>

        {/* Link Row */}
        <div className="mt-14 pt-8 border-t border-border/60 text-center">
          <p className="text-sm text-muted-foreground">
            Explore how I prepare B2B and retail brands for conversational and generative search on my dedicated{" "}
            <Link to="/AISearchOptimisationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
              AI search optimisation service
              <ArrowRight className="size-3.5" />
            </Link>
          </p>
        </div>
      </div>
    </section>;
}